import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon } from "lucide-react";
import { z } from "zod";
import { createUserUsersPost, readClientsClientsGet } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { PricePreferenceAccordion } from "@/components/admin/UserTable/PricePreference/PicePreferenceAccordion";
import {
  estimatedPricePreferenceDefaultvalues,
  estimatedPriceUpdatePreferencesSchema,
} from "@/components/admin/UserTable/PricePreference/schema";
import { checkSuperUserPermission, cn, PERMISSIONS, permissionSchema } from "@/lib/utils";

const userClientLinkSchema = z.object({
  client_id: z.number({ error: "Please select a client" }).min(1, "Please select a client"),
  permissions: z.array(permissionSchema).min(1, "At least one permission is required"),
  estimated_price_update_preferences: estimatedPriceUpdatePreferencesSchema,
  cc_recipients: z.array(z.string().email()).default([]),
  bcc_recipients: z.array(z.string().email()).default([]),
});

const createUserSchema = z.object({
  email: z.string().email(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  userClientLinks: z.array(userClientLinkSchema).min(1, "At least one client link is required"),
  welcome_email_language: z.enum(["nl", "en"]).default("nl"),
});

export type CreateUserFormValues = z.infer<typeof createUserSchema>;

export const Route = createFileRoute("/_superuser/admin/users/new")({
  component: CreateUserPage,
  beforeLoad: () => {
    checkSuperUserPermission("user-management");
  },
});

function CreateUserPage() {
  const [openPickers, setOpenPickers] = useState<{ [key: number]: boolean }>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const form = useForm<z.input<typeof createUserSchema>, unknown, CreateUserFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      welcome_email_language: "nl",
      userClientLinks: [
        {
          client_id: undefined,
          permissions: PERMISSIONS.map((permission) => permission.id),
          estimated_price_update_preferences: estimatedPricePreferenceDefaultvalues,
          cc_recipients: [],
          bcc_recipients: [],
        },
      ],
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "userClientLinks",
  });

  const { data: clientsData } = useQuery({
    queryKey: ["clients"],
    queryFn: () => readClientsClientsGet(),
  });

  const clientOptions =
    clientsData?.data.filter(
      (client) => !form.getValues("userClientLinks").some((link) => link.client_id === client.id),
    ) || [];

  const createUserMutation = useMutation({
    mutationFn: async (data: CreateUserFormValues) => {
      const response = await createUserUsersPost({
        requestBody: {
          user_in: {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
          },
          client_links: data.userClientLinks.map((userClientLinkInput) => {
            return {
              client_id: userClientLinkInput.client_id,
              permissions: userClientLinkInput.permissions || [],
              estimated_price_update_preferences: userClientLinkInput.estimated_price_update_preferences || null,
              cc_recipients: [...new Set(userClientLinkInput.cc_recipients)],
              bcc_recipients: [...new Set(userClientLinkInput.bcc_recipients)],
            };
          }),
        },
        welcomeEmailLanguage: data.welcome_email_language,
      });
      return response;
    },
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate({ to: "/admin/users" });
      toast({
        title: "Success",
        description: "User created successfully",
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      let errorMessage = "Failed to create user. Please try again later.";

      if (error?.body?.detail) {
        errorMessage = error.body.detail;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  async function onSubmit(data: CreateUserFormValues) {
    await createUserMutation.mutateAsync(data);
  }

  const formErrors = form.formState.errors;
  const hasFormErrors = Object.keys(formErrors).length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Card className="flex flex-col gap-2">
        <CardHeader className="p-4">
          <CardTitle>Create User</CardTitle>
          <CardDescription>Fill in the user details and assign their permissions.</CardDescription>
        </CardHeader>
        <CardContent className="p-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="welcome_email_language"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Welcome Email Language</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="nl">Dutch</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="space-y-2 rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Client Link {index + 1}</h4>
                      {fields.length > 1 && (
                        <Button type="button" variant="destructive" size="sm" onClick={() => remove(index)}>
                          Remove
                        </Button>
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name={`userClientLinks.${index}.client_id`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Client</FormLabel>
                          <FormControl>
                            <Popover
                              open={openPickers[index]}
                              onOpenChange={(open) =>
                                setOpenPickers((prev) => ({
                                  ...prev,
                                  [index]: open,
                                }))
                              }
                              modal
                            >
                              <PopoverTrigger asChild>
                                <Button variant="outline" role="combobox" className="w-full justify-between">
                                  {clientsData?.data?.find((client) => client.id === field.value)?.client_name ||
                                    "Select client..."}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-0" align="start" side="bottom" sideOffset={4}>
                                <Command>
                                  <CommandInput placeholder="Search client..." className="h-9" />
                                  <CommandEmpty>No client found.</CommandEmpty>
                                  <CommandGroup>
                                    <CommandList>
                                      {clientOptions.map((client) => (
                                        <CommandItem
                                          key={client.id}
                                          value={client.client_name}
                                          onSelect={() => {
                                            field.onChange(client.id);
                                            setOpenPickers((prev) => ({
                                              ...prev,
                                              [index]: false,
                                            }));
                                          }}
                                        >
                                          <CheckIcon
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              field.value === client.id ? "opacity-100" : "opacity-0",
                                            )}
                                          />
                                          {client.client_name}
                                        </CommandItem>
                                      ))}
                                    </CommandList>
                                  </CommandGroup>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`userClientLinks.${index}.permissions`}
                      render={({ field }) => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel className="flex items-center justify-between">
                              Permissions
                              {form.formState.errors.userClientLinks?.[index]?.permissions && (
                                <span className="text-sm font-normal text-destructive">
                                  {form.formState.errors.userClientLinks?.[index]?.permissions?.message}
                                </span>
                              )}
                            </FormLabel>
                            <FormDescription>Select the permissions for this client link.</FormDescription>
                          </div>
                          <div
                            className={cn(
                              "grid grid-cols-2 gap-4",
                              form.formState.errors.userClientLinks?.[index]?.permissions &&
                                "rounded-md border border-destructive/50 p-4",
                            )}
                          >
                            {PERMISSIONS.map((permission) => (
                              <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(permission.id)}
                                    onCheckedChange={(checked) => {
                                      const currentPermissions = field.value || [];
                                      return checked
                                        ? field.onChange([...currentPermissions, permission.id])
                                        : field.onChange(
                                            currentPermissions?.filter((value) => value !== permission.id),
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">{permission.label}</FormLabel>
                              </FormItem>
                            ))}
                          </div>
                        </FormItem>
                      )}
                    />
                    <PricePreferenceAccordion index={index} />
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    append({
                      // @ts-expect-error - client_id will be validated on submit, allowing undefined for initial state
                      client_id: undefined,
                      permissions: PERMISSIONS.map((permission) => permission.id),
                      estimated_price_update_preferences: estimatedPricePreferenceDefaultvalues,
                      cc_recipients: [],
                      bcc_recipients: [],
                    })
                  }
                >
                  Add Another Client Link
                </Button>
              </div>

              <CardFooter className="flex flex-col gap-4 px-0">
                {hasFormErrors && (
                  <div className="space-y-2 rounded-md bg-destructive/15 p-3">
                    <div className="text-sm font-medium text-destructive">
                      Please fix the following errors before submitting:
                    </div>
                    <ul className="list-inside list-disc space-y-1 text-sm text-destructive/90">
                      {formErrors?.email && <li>Email: {formErrors?.email?.message}</li>}
                      {formErrors?.first_name && <li>First Name: {formErrors?.first_name?.message}</li>}
                      {formErrors?.last_name && <li>Last Name: {formErrors?.last_name?.message}</li>}
                      {formErrors?.welcome_email_language && (
                        <li>Welcome Email Language: {formErrors?.welcome_email_language?.message}</li>
                      )}
                      {formErrors?.userClientLinks && (
                        <>
                          {Array.isArray(formErrors?.userClientLinks) &&
                            formErrors?.userClientLinks.map(
                              (linkError, index) =>
                                linkError && (
                                  <li key={index}>
                                    Client Link {index + 1}:
                                    <ul className="list-circle ml-4 list-inside">
                                      {linkError?.client_id && <li>{linkError?.client_id?.message}</li>}
                                      {linkError?.permissions && <li>{linkError?.permissions?.message}</li>}
                                      {linkError?.cc_recipients && (
                                        <li>CC Recipients: {linkError?.cc_recipients?.message}</li>
                                      )}
                                      {linkError?.bcc_recipients && (
                                        <li>BCC Recipients: {linkError?.bcc_recipients?.message}</li>
                                      )}
                                    </ul>
                                  </li>
                                ),
                            )}
                          {!Array.isArray(formErrors?.userClientLinks) && formErrors?.userClientLinks?.message && (
                            <li>{formErrors?.userClientLinks?.message}</li>
                          )}
                        </>
                      )}
                      {formErrors?.root && <li>{formErrors?.root.message}</li>}
                    </ul>
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={createUserMutation.isPending || hasFormErrors}>
                  {createUserMutation.isPending ? "Creating..." : "Create User"}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
